/**
 * "This session owes the reader their recovery codes."
 *
 * Set when MFA enrollment is confirmed, honoured once the session has passed
 * MFA, cleared when the codes screen is reached.
 *
 * It lives in session storage rather than in the URL because the journey from
 * enrollment to the codes crosses three guards, any of which may redirect with
 * a `<Navigate>` of its own. A destination carried as `?next=` is dropped by
 * the first one that rebuilds the path without it — and which redirect runs
 * first depends on whether the token update and the route change land in the
 * same render, which is not something to rely on. A fact in storage survives
 * every one of them.
 *
 * Session-scoped on purpose: it is about the enrollment happening right now,
 * and it should not outlive the tab.
 */

const KEY = "everythingcloud-pending-recovery-codes";

/** Storage throws in some embedded contexts; the flow must not depend on it. */
function safely<T>(fn: () => T, fallback: T): T {
  try {
    return fn();
  } catch {
    return fallback;
  }
}

export function markRecoveryCodesPending(): void {
  safely(() => sessionStorage.setItem(KEY, "1"), undefined);
}

export function recoveryCodesPending(): boolean {
  return safely(() => sessionStorage.getItem(KEY) === "1", false);
}

export function clearRecoveryCodesPending(): void {
  safely(() => sessionStorage.removeItem(KEY), undefined);
}

/**
 * "This session was sent to confirm its identity before it could start
 * enrolling." Set when starting enrollment is refused with `step_up_required`,
 * honoured once the challenge is passed, cleared when the enrollment screen is
 * reached. A key of its own, so it and the recovery codes debt never overwrite
 * each other.
 */
const ENROLL_KEY = "everythingcloud-pending-mfa-enroll";

export function markEnrollPending(): void {
  safely(() => sessionStorage.setItem(ENROLL_KEY, "1"), undefined);
}

export function enrollPending(): boolean {
  return safely(() => sessionStorage.getItem(ENROLL_KEY) === "1", false);
}

export function clearEnrollPending(): void {
  safely(() => sessionStorage.removeItem(ENROLL_KEY), undefined);
}

/** Drops both debts. They belong to one identity, so every change of identity
 *  calls this. */
export function clearPendingMfaReturns(): void {
  clearRecoveryCodesPending();
  clearEnrollPending();
}
